/** Format seconds into "Xh Ym" or "Ym" or "0m" */
export function formatDuration(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds <= 0) return '0m';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Format seconds into "HH:MM:SS" for live timers */
export function formatTimer(totalSeconds: number): string {
  if (totalSeconds <= 0) return '00:00:00';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

/** Format a Date to "9:05 AM" style */
export function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

/** Format a Date to "Jan 15" or "Jan 15, 2024" */
export function formatDate(date: Date, includeYear = false): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (includeYear) opts.year = 'numeric';
  return date.toLocaleDateString('en-US', opts);
}

/** Get start of day for a date */
export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Get end of day for a date */
export function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

/** Get YYYY-MM-DD string from Date (uses local time, not UTC) */
export function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Compute elapsed seconds for an active clock event */
export function getActiveClockSeconds(
  event: {
    startTime: number;
    endTime: number | null;
    isPaused?: boolean;
    workSeconds?: number;
    deductedBreakSeconds?: number;
  } | null,
  nowMs: number,
): number {
  if (!event || event.endTime != null) return 0;

  // Paused: freeze at the last computed work snapshot
  if (event.isPaused) return Math.max(0, event.workSeconds ?? 0);

  // Active: tick live — shift span minus already-deducted meal breaks
  const deducted = event.deductedBreakSeconds ?? 0;
  return Math.max(0, Math.floor((nowMs - event.startTime) / 1000) - deducted);
}
